import React, { useState, useContext, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { ConfigContext } from '../../../../../Api/global/ApiContext';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from "reactstrap";
import { Button, DescriptionList, Field, Loader, Select } from "../../../../../ui";
import CalendarLegend from "../ui-pending/CalendarLegend";
import "./ae-calendar.scss";

import { AcademicExpertCalendar, addAeSlot } from "../../../../../Api/apiUtils";
import EventModal from "./modal";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../../../utils/mixpanelTracking";

interface AeProps {
    teacherId: any;
    teacherDetails?: any;
}

interface Event {
    id: string;
    title: string;
    start: string;
    end: string;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
}

interface AirlineCategory {
    id: number;
    name: string;
    categoryId: number;
}

const normalizeCategoryKey = (value: string) => value.trim().toLowerCase().replace(/[\s_-]+/g, "");

// The profile names some categories differently from the config dropdown:
// "Instrument-Guitar" vs "Guitar", "Western Vocals" vs "Western Vocal".
const profileCategoryKey = (value: string) =>
    normalizeCategoryKey(value).replace(/^instrument/, "").replace(/vocals$/, "vocal");

// The teacher's saved category as a config categoryId (the dropdown value): the profile api
// returns the category id when it has one, otherwise only its name (e.g. "Vocal-North").
const resolveTeacherCategoryId = (teacherDetails: any, categories: AirlineCategory[]): string => {
    const details = teacherDetails?.teacherProfileDetails || {};
    const directId =
        details?.aeCourseCategoryId ??
        details?.courseCategoryId ??
        teacherDetails?.aeCourseCategoryId ??
        teacherDetails?.courseCategoryId;
    if (directId != null && directId !== "") {
        const byId = categories.find((category) => String(category.categoryId) === String(directId));
        if (byId) return String(byId.categoryId);
    }
    const categoryName = String(teacherDetails?.courseCategory || details?.courseCategory || "").trim();
    if (!categoryName) return "";
    const teacherKey = profileCategoryKey(categoryName);
    const byName = categories.find(
        (category) => normalizeCategoryKey(category.name || "") === teacherKey
    );
    return byName ? String(byName.categoryId) : "";
};

const AeCalendar: React.FC<AeProps> = ({ teacherId, teacherDetails }) => {
    const config = useContext(ConfigContext);
    const AirlineCourseCategory: AirlineCategory[] = config?.AirlineCourseCategory || [];
    console.log("AirlineCourseCategory", config)
    const [currentView, setCurrentView] = useState("dayGridMonth"); // Default view
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [events, setEvents] = useState<Event[]>([]);
    const [fromDate, setFromDate] = useState<string>("");
    const [toDate, setToDate] = useState<string>("");
    const [calendarRef, setCalendarRef] = useState<any>(null);
    const [eventDetails, setEventDetails] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState({ slotDate: "", startTime: "", endTime: "", ishalfHourSlot: true });
    const [maxNumberOfSlots, setMaxNumberOfSlots] = useState<string>("");
    const [isInternationalSlot, setIsInternationalSlot] = useState(false);
    const teacherCategoryId = resolveTeacherCategoryId(teacherDetails, AirlineCourseCategory);

    // Show the category already mapped to the teacher instead of "-- Select a Category --";
    // a category the user picks by hand is left alone.
    useEffect(() => {
        if (teacherCategoryId) {
            setSelectedCategory((current) => current || teacherCategoryId);
        }
    }, [teacherCategoryId]);

    // Load the slots for the defaulted category without a manual Submit click; handleDatesSet
    // already submits once a category is set, so this runs only for the first load.
    const initialSubmitDone = useRef(false);
    useEffect(() => {
        if (initialSubmitDone.current || !selectedCategory || !fromDate || !toDate) return;
        initialSubmitDone.current = true;
        handleSubmit(fromDate, toDate);
    }, [selectedCategory, fromDate, toDate, teacherId]);
    const aeLegendItems = [
        { label: "Completed", color: "#7b7e80" },
        { label: "Booked", color: "#ffc306" },
        { label: "Free", color: "#b3c98a" },
        { label: "Cancelled", color: "#ff5151" },
    ];

    const formatLocalDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const splitIntoThirtyMinuteSlots = (
        slotDate: string,
        startTime: string,
        endTime: string,
        internationalSlot: boolean
    ) => {
        const toMinutes = (time: string) => {
            const [hours, minutes] = time.split(":").map(Number);
            return hours * 60 + minutes;
        };

        const toTimeString = (totalMinutes: number) => {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
        };

        const slots: Array<{
            isInternationalSlot: boolean;
            slotDate: string;
            startTime: string;
            endTime: string;
        }> = [];

        let cursor = toMinutes(startTime);
        const endMinutes =
            endTime === "24:00:00" ? 24 * 60 : toMinutes(endTime);

        while (cursor + 30 <= endMinutes) {
            slots.push({
                isInternationalSlot: internationalSlot,
                slotDate,
                startTime: toTimeString(cursor),
                // the last slot of the day ends at 00:00:00, how saved slots come back (see events below)
                endTime: toTimeString((cursor + 30) % (24 * 60)),
            });
            cursor += 30;
        }

        return slots;
    };


    const handleCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedCategory(event.target.value);
        // never show the previous category's slots under the new one
        setEvents([]);
    };

    const handleViewChange = (view: any) => {
        setCurrentView(view.view.type);
    };
    const handleDatesSet = (arg: { start: Date; end: Date }) => {
        const updatedFromDate = formatLocalDate(arg.start);
        const inclusiveEndDate = new Date(arg.end);
        inclusiveEndDate.setDate(inclusiveEndDate.getDate() - 1);
        const updatedToDate = formatLocalDate(inclusiveEndDate);

        setFromDate(updatedFromDate);
        setToDate(updatedToDate);

        if (selectedCategory) {
            initialSubmitDone.current = true;
            handleSubmit(updatedFromDate, updatedToDate);
        }
    };

    const handleEventClick = (info: any) => {
        setEventDetails(info.event.extendedProps);
        setIsModalOpen(true); 
    };
    const handleSubmit = async (fromDate: string, toDate: string) => {
        if (!selectedCategory || !teacherId || !fromDate || !toDate) {
            toast.error("Please select a category.");
            return;
        }

        const selectedCategoryNumber = Number(selectedCategory);
        const selectedConfigCategory = AirlineCourseCategory.find(
            (item) => item.categoryId === selectedCategoryNumber
        );

        if (!selectedConfigCategory) {
            toast.error("Selected category is invalid. Please reselect and try again.");
            return;
        }
    
        try {
            setLoading(true);
            const response = await AcademicExpertCalendar({
                teacherId,
                fromDate,
                toDate,
                parentTagId: selectedConfigCategory.id,
            });
    
            console.log("API Response:", response); 
    
            // a 204 (no slots for this category) comes back as "", which has no .map
            const slots = Array.isArray(response) ? response : [];
            const mappedEvents = slots.map((event: any, index: number) => {
                const status = String(event.slotStatus || "").toLowerCase();
                const isBooked = status.includes("booked") || status.includes("scheduled");
                const isCompleted = status.includes("completed");
                const isCancelled = status.includes("cancelled") || status.includes("canceled");
                const isFree = status.includes("free");
                const backgroundColor = isBooked
                    ? "#ffc306"
                    : isCompleted
                        ? "#7b7e80"
                        : isCancelled
                            ? "#ff5151"
                            : isFree
                                ? "#b3c98a"
                                : "#b3c98a";
                const title = isBooked
                    ? "Booked"
                    : isCompleted
                        ? "Completed"
                        : isCancelled
                            ? "Cancelled"
                            : isFree
                                ? "Free Slot"
                                : "Slot";

                return {
                    // FullCalendar needs stable unique ids; API `id` is often missing/duplicated.
                    id: String(
                        event.airlineTeacherSlotId ||
                        event.id ||
                        `${event.slotDate}-${event.startTime}-${event.endTime}-${index}`
                    ),
                    title,
                    start: `${event.slotDate}T${event.startTime}`,
                    end: `${event.slotDate}T${event.endTime === "00:00:00" ? "23:59:59" : event.endTime}`,
                    backgroundColor,
                    borderColor: backgroundColor,
                    textColor: "#ffffff",
                    allDay: false,
                    extendedProps: { ...event },
                };
            });
    
            console.log("Mapped Events:", mappedEvents); 
    
            setEvents(mappedEvents); 
    
        } catch (error) {
            console.error("Failed to fetch calendar events:", error);
            // a failed fetch must not leave the previous category's slots on screen
            setEvents([]);
        } finally {
            setLoading(false);
          }
    };

    const handleSelect = (selectInfo: any) => {
        // Ignore month/all-day/date-click style selections.
        if (selectInfo.allDay || selectInfo.view?.type === "dayGridMonth") {
            selectInfo.view.calendar.unselect();
            return;
        }
        if (!selectedCategory) {
            toast.error("Please select a category first.");
            selectInfo.view.calendar.unselect();
            return;
        }
        const startDate = new Date(selectInfo.startStr);
        const endDate = new Date(selectInfo.endStr);
        const durationMinutes = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
        const currentTime = new Date();

        if (startDate < currentTime) {
            toast.error("You can only select slots after the current time.");
            selectInfo.view.calendar.unselect();
            return;
        }

        if (durationMinutes <= 0 || startDate.getTime() === endDate.getTime() || durationMinutes % 30 !== 0) {
            toast.error("Please select slots in 30-minute increments.");
            selectInfo.view.calendar.unselect();
            return;
        }
        // A selection that runs to midnight ends at 00:00 of the next day; mark it as 24:00:00 so it
        // is split as the end of the slot's own day (e.g. 11:30 PM - 12:00 AM), not as minute 0.
        // Only the next day's midnight: a selection spanning further days is left as before.
        const startDayEnd = new Date(startDate);
        startDayEnd.setHours(24, 0, 0, 0);
        const endsAtMidnight = endDate.getTime() === startDayEnd.getTime();
        const endTimeStr = endsAtMidnight ? "24:00:00" : endDate.toTimeString().split(" ")[0];
        setSelectedSlot({
            slotDate: selectInfo.startStr.split("T")[0],
            startTime: startDate.toTimeString().split(" ")[0],
            endTime: endTimeStr,
            ishalfHourSlot: durationMinutes <= 30,
        });
        setMaxNumberOfSlots("");
        setIsInternationalSlot(false);
        setIsConfirmModalOpen(true);
        selectInfo.view.calendar.unselect();
    };

    const handleConfirmAeSlot = async () => {
        if (!maxNumberOfSlots) {
            toast.error("Please select max booking per slots.");
            return;
        }
        const slotStartDate = new Date(`${selectedSlot.slotDate}T${selectedSlot.startTime}`);
        if (slotStartDate < new Date()) {
            toast.error("Cannot create slot in past date/time.");
            setIsConfirmModalOpen(false);
            return;
        }
        const selectedCategoryNumber = Number(selectedCategory);
        const selectedConfigCategory = AirlineCourseCategory.find((item) => item.categoryId === selectedCategoryNumber);
        if (!selectedConfigCategory) {
            toast.error("Selected category is invalid.");
            return;
        }
        try {
            setLoading(true);
            const dividedSlots = splitIntoThirtyMinuteSlots(
                selectedSlot.slotDate,
                selectedSlot.startTime,
                selectedSlot.endTime,
                isInternationalSlot
            );
            if (dividedSlots.length === 0) {
                toast.error("Please select a valid 30-minute slot range.");
                return;
            }
            await addAeSlot({
                teacherId,
                clientId: 1,
                ishalfHourSlot: true,
                maxNumberOfSlots: Number(maxNumberOfSlots),
                categoryId: Number(selectedConfigCategory.categoryId || selectedConfigCategory.id),
                slots: dividedSlots,
            });
            toast.success("AE slot added successfully.");
            trackEvent(MixpanelEvents.AE_CALENDAR_SLOT_ADDED, {
              teacherId: String(teacherId ?? ""),
              categoryId: Number(selectedConfigCategory.categoryId || selectedConfigCategory.id),
              slotDate: selectedSlot.slotDate,
              ishalfHourSlot: selectedSlot.ishalfHourSlot,
              maxNumberOfSlots: Number(maxNumberOfSlots),
              isInternationalSlot,
            });
            setIsConfirmModalOpen(false);
            await handleSubmit(fromDate, toDate);
        } catch (error) {
            console.error("Failed to add AE slot:", error);
            trackEvent(MixpanelEvents.AE_CALENDAR_SLOT_ADD_FAILED, {
              teacherId: String(teacherId ?? ""),
            });
            const data = (error as any)?.response?.data;
            const apiMessage =
                typeof data === "string"
                    ? data
                    : data?.message || data?.error || data?.errorMessage || data?.detail;
            toast.error(apiMessage || "Failed to add AE slot.");
        } finally {
            setLoading(false);
        }
    };

    const handleMoreLinkClick = (date:any) => {
        if (calendarRef) {
          calendarRef.getApi().changeView("timeGridDay", date);
        }
        if (selectedCategory) {
            const clickedDate = formatLocalDate(new Date(date));
            handleSubmit(clickedDate, clickedDate);
        }
      };
      const handleDateClick = (arg: any) => {
        if (calendarRef) {
          calendarRef.getApi().changeView('timeGridDay', arg.date);
        }
        if (selectedCategory) {
            const clickedDate = formatLocalDate(new Date(arg.date));
            handleSubmit(clickedDate, clickedDate);
        }
      };

    return (
        <div className="tp-ae">
            <div className="tp-ae__toolbar">
                <Field label="Category" id="courseCategory" className="tp-ae__category">
                    {(control) => (
                        <Select
                            {...control}
                            value={selectedCategory}
                            onChange={handleCategoryChange}
                            // keyed by categoryId: the Western categories share the same id (62)
                            options={[
                                { value: "", label: "-- Select a Category --" },
                                ...AirlineCourseCategory.map((category) => ({
                                    value: String(category.categoryId),
                                    label: category.name,
                                })),
                            ]}
                        />
                    )}
                </Field>
                <Button variant="primary" onClick={() => handleSubmit(fromDate, toDate)}>Submit</Button>
                {/* Swatches mirror the FullCalendar event colours set in handleSubmit. */}
                <CalendarLegend items={aeLegendItems} label="AE calendar legend" className="tp-ae__legend" />
            </div>
            <div className="tp-ae__stage">
            {loading && (
                <div className="tp-ae__overlay">
                    <Loader label="Loading calendar…" />
                </div>
            )}
            <FullCalendar
    initialView={currentView}
    initialDate={new Date()}
    headerToolbar={{
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay",
    }}
    views={{
        timeGridWeek: {
            dayHeaderFormat: { weekday: "short", day: "numeric" },
        },
        timeGridDay: {
            dayHeaderFormat: { weekday: "short", day: "numeric" },
        },
    }}
    ref={setCalendarRef}
    editable={false}
    droppable={false}
    selectable={true}
    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
    dayMaxEventRows={2}
    dayMaxEvents={2}
    eventOrder={(a: any, b: any) => {
        const aBooked = String(a.title || "").toLowerCase() === "booked" ? 0 : 1;
        const bBooked = String(b.title || "").toLowerCase() === "booked" ? 0 : 1;
        if (aBooked !== bBooked) return aBooked - bBooked;
        return (a.start?.getTime?.() || 0) - (b.start?.getTime?.() || 0);
    }}
    allDaySlot={false}
    slotDuration="00:15:00"
    slotLabelInterval="00:30:00"
    scrollTime="00:00:00"
    height="auto"
    contentHeight="auto"
    events={events}
    dateClick={handleDateClick}
    datesSet={handleDatesSet}
    moreLinkClick={(info) => handleMoreLinkClick(info.date)}
    eventClick={handleEventClick}
    select={handleSelect}
    viewDidMount={handleViewChange} 
    eventContent={(arg) => {
        const currentView = arg.view.type;
        const startTime = arg.event.start?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const endTime = arg.event.end?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const eventBg = arg.event.backgroundColor || "#2f9f90";
        return (
            <div
                style={{
                    textAlign: "center",
                    backgroundColor: eventBg,
                    border: `1px solid ${arg.event.borderColor || eventBg}`,
                    borderRadius: "6px",
                    padding: "2px 6px",
                    color: "#ffffff",
                    fontWeight: 600,
                    fontSize: "11px",
                    lineHeight: 1.2,
                    width: "100%",
                }}
            >
                {(currentView === "timeGridDay" || currentView === "timeGridWeek") && (
                    <div>{startTime} - {endTime}</div>
                )}
                <div>{arg.event.title}</div>
            </div>
        );
    }}
/>
            </div>

<EventModal
    isOpen={isModalOpen}
    toggle={() => setIsModalOpen(false)}
    eventDetails={eventDetails}
    fetchCalendar={() => handleSubmit(fromDate, toDate)}

    fromDate={fromDate} 
    toDate={toDate}
    selectedCategory={selectedCategory}
    teacherId={teacherId}
/>
<Modal isOpen={isConfirmModalOpen} toggle={() => setIsConfirmModalOpen(false)}>
    <ModalHeader toggle={() => setIsConfirmModalOpen(false)}>Confirm selected date and time</ModalHeader>
    <ModalBody className="tp-ae-confirm">
        <DescriptionList
            items={[
                { label: "Date", value: selectedSlot.slotDate },
                { label: "Time range", value: `${selectedSlot.startTime} - ${selectedSlot.endTime}` },
            ]}
        />
        <Field label="Max booking per slots" required>
            {(control) => (
                <Select
                    {...control}
                    value={maxNumberOfSlots}
                    onChange={(e) => setMaxNumberOfSlots(e.target.value)}
                    options={[
                        { value: "", label: "select slots" },
                        ...[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) })),
                    ]}
                />
            )}
        </Field>
        <FormGroup check>
            <Label check>
                <Input
                    type="checkbox"
                 className="levelChk"
                    checked={isInternationalSlot}
                    onChange={(e) => setIsInternationalSlot(e.target.checked)}
                />{" "}
                International Slot
            </Label>
        </FormGroup>
    </ModalBody>
    <ModalFooter>
        <Button variant="secondary" onClick={() => setIsConfirmModalOpen(false)}>
            Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirmAeSlot} disabled={loading}>
            {loading ? "Processing..." : "OK"}
        </Button>
    </ModalFooter>
</Modal>
        </div>
    );
};

export default AeCalendar;